import { parseStatic, parseStatics } from '@core/utils/assets.utils'
import { StaticOrientation } from '@core/enums/statics.enum'
import { LastNMatchesResponse, Match, MatchTeam, MatchTeamTrend, MatchTrend } from '@core/models/match.models'
import { MetricMap } from '@core/models/metric.models'
import { MatchTab } from '@pages/matches/enums/matches.enum'
import { safeObjectData } from '@core/utils/object.utils'
import { Player } from '@core/models/player.models'
import { DemarcationPipe } from '@shared/pipes/demarcation.pipe'
import { PLAYER_DEMARCATION_FALLBACK_KEY, PLAYER_DEMARCATION_KEYS } from '@core/constants/player.constants'
import { resolvePlayerDetailsNavigationUrl, resolvePlayerMatchNavigationUrl } from '@core/utils/router.utils'
import { Aggregation } from '@features/aggregations/models/aggregations.models'
import { MatchState, TeamType } from '@mediacoach/ui'
import { GridFieldsAndProps } from '@pages/match-detail/models/match-detail.models'
import { List, ListType } from '@core/models/list.models'
import { TEAMS } from '@core/constants/competition.constants'

export const mapMatch = (match: Match): Match => ({
  ...match,
  homeTeam: mapTeamMatch(match.homeTeam, match),
  awayTeam: mapTeamMatch(match.awayTeam, match),
  isLive: !!(match?.state && (match?.state?.minute || match?.state?.name === MatchState.LineupReady))
})

export const mapTeamMatch = (team: MatchTeam, match: Match): MatchTeam => ({
  id: team.teamId,
  ...team,
  name: team.shortName || team.name,
  ...parseStatics(team.statics, [StaticOrientation.Portrait]),
  seasonId: match.seasonId,
  competitionId: match.competitionId
})

export const sortByMatchdayDescDateDesc = (items: Match[]): Match[] => {
  return [...items].sort((a, b) => {
    if (a.matchdayNumber !== b.matchdayNumber) {
      return -(a.matchdayNumber - b.matchdayNumber)
    }

    if (a?.schedule?.translated?.date !== b?.schedule?.translated?.date) {
      return -(a?.schedule?.translated?.date?.valueOf() - b?.schedule?.translated?.date?.valueOf())
    }
  })
}

export function updateMatchWithLiveMatch(match: Match, liveMatch: Match): Match {
  return {
    ...match,
    homeTeam: { ...match.homeTeam, ...liveMatch.homeTeam },
    awayTeam: { ...match.awayTeam, ...liveMatch.awayTeam },
    state: liveMatch.state,
    isLive: liveMatch.isLive
  }
}

export const getMatchIdByLocation = (): string => location.pathname.replace('/matches/', '').split('/')[0]

export const buildMatchTabListFromMetricMap = (metricMap: MetricMap): string[] => {
  const categories = metricMap?.metricCategories?.map((c) => c.key) ?? []

  return [...Object.values(MatchTab), ...categories]
}

const buildMatchTeamTrend = (teamTrend: MatchTeamTrend, team: MatchTeam): MatchTrend => {
  return {
    ...teamTrend,
    trend: {
      img: parseStatic(team.statics || [], StaticOrientation.Portrait),
      name: team?.shortName,
      data: teamTrend?.lastMatchResults || [],
      position: teamTrend?.standings?.position
    }
  }
}

export const buildMatchTrends = (match: Match, lastMatches: LastNMatchesResponse): MatchTrend[] => {
  if (lastMatches?.homeTeam && lastMatches?.awayTeam && match) {
    return [
      buildMatchTeamTrend(lastMatches.homeTeam, match.homeTeam),
      buildMatchTeamTrend(lastMatches.awayTeam, match.awayTeam)
    ]
  }

  return []
}

export const resolveMatchTeamByDirection = (
  direction: 'left' | 'right',
  leftTeam: MatchTeam,
  rightTeam: MatchTeam,
  match: Match
): { team: MatchTeam; direction: 'left' | 'right' } => {
  const _team = direction === 'left' ? leftTeam : rightTeam

  return {
    team: _team.teamId === match.homeTeam?.teamId ? match.awayTeam : match.homeTeam,
    direction
  }
}

export const mapMatchTeamTypeToTeamKey = (team: TeamType) => team + 'Team'

const mapPlayerToListItem = (
  player: Player,
  match: Match,
  team: MatchTeam,
  demarcationPipe: DemarcationPipe
): Partial<Player> => {
  return {
    id: player.id,
    teamId: team.teamId,
    competitionId: match.competitionId,
    seasonId: match.seasonId,
    imgUrl: (safeObjectData(player, 'statics', []).find(({ orientation }) => orientation === 'Portrait') || {}).url,
    shirtNumber: safeObjectData(player, 'shirtNumber', '-'),
    name: safeObjectData(player, 'formattedName', '-'),
    position: demarcationPipe.transform(player, PLAYER_DEMARCATION_KEYS, PLAYER_DEMARCATION_FALLBACK_KEY, ' '),
    navigateUrl: team.lineup
      ? resolvePlayerMatchNavigationUrl(match.id, team.teamId, player.id)
      : resolvePlayerDetailsNavigationUrl(match.seasonId, match.competitionId, team.teamId, player.id)
  }
}
const mapMatchPlayersAsList = (
  match: Match,
  isStarter: boolean,
  _demarcationPipe: DemarcationPipe
): { home: List<Partial<Player>>; away: List<Partial<Player>> } => {
  return TEAMS.reduce(
    (obj, teamKey) => {
      const matchTeam: MatchTeam = match[mapMatchTeamTypeToTeamKey(teamKey)]
      return {
        ...obj,
        [teamKey]: {
          type: ListType.Players,
          groups: [
            {
              items: ((matchTeam.lineup || matchTeam.squad || {}).players || [])
                .filter((player: Player) => !!player.starter === isStarter)
                .map((player: Player) => mapPlayerToListItem(player, match, matchTeam, _demarcationPipe))
            }
          ]
        }
      }
    },
    {} as { home: List<Partial<Player>>; away: List<Partial<Player>> }
  )
}

export const mapMatchPlayerList = (match: Match, _demarcationPipe: DemarcationPipe) =>
  ['starters', 'substitutes'].reduce(
    (obj, squadType) => ({
      ...obj,
      [squadType]: mapMatchPlayersAsList(match, squadType === 'starters', _demarcationPipe)
    }),
    {}
  )

const getFieldsAndProps = (data, accessDataKey: string): GridFieldsAndProps => {
  const metricKeys = safeObjectData(data || {}, accessDataKey, []).map(({ key }) => key)
  return {
    fields: [...metricKeys],
    props: [...metricKeys]
  }
}

export const mapMatchPlayersToStatsTable = (
  home: Player[],
  away: Player[],
  returnData: TeamType[] = [],
  isSwitchable: boolean,
  switcherOptions: Aggregation[] = []
) => {
  const switcher = [
    { id: 'Match', text: 'MTR_COMMON_MATCH_VALUE' },
    { id: 'Average', text: 'MTR_COMMON_AVERAGE' },
    { id: 'Accumulated', text: 'MTR_COMMON_ACCUMULATED' }
  ]

  const DATA_TO_PROCESS = [
    { id: TeamType.Home, value: home },
    { id: TeamType.Away, value: away }
  ]

  return {
    isSwitchable,
    switcherValues: isSwitchable ? switcher.filter(({ id }) => switcherOptions.includes(id as Aggregation)) : [],
    ...DATA_TO_PROCESS.filter((team) => returnData.includes(team.id)).reduce(
      (prev, current) => ({
        ...prev,
        [current.id]: getFieldsAndProps(current.value, 'players.0.listMetricResults')
      }),
      {}
    )
  }
}
